// Resolving a marker to the feed id you'd view for it — and, when that fails, saying why.
//
// The handoff says each input "carries its member feed's identity", but the captured response keys
// `inputs` by bare member name — `ebit_trend` — with no id inside, so the normalizer falls back to
// the key (src/services/feedView.js). Whether that key is itself a viewable feed id is the one
// question the plan's step 0 probe was meant to settle, and it hasn't been run.
//
// So the resolution lives here, in one function, with the feed list as a corroborating source:
//   - the key is a listed feed id            → use it;
//   - exactly one listed id ends with the key → use that (score_spec_<key>[__<name>] shapes);
//   - nothing matches, and no list is loaded  → try the key anyway, and let a 422 say otherwise;
//   - nothing matches, but a list IS loaded   → unresolvable, with a reason to show the analyst.
// Changing this to whatever the probe finds is a one-function edit.
//
// An unresolvable marker used to render inert, which left the analyst with no way to ask why. The
// reason and the candidate ids travel out of here instead, so the cell can stay pressable and
// explain itself (DrillDownUnavailableModal).

export const DRILL_DOWN_REASONS = {
  // The marker arrived with neither a feed id nor a key — there is nothing to name, let alone open.
  NO_IDENTITY: 'no_identity',
  // A feed list is loaded and nothing in it looks like this key.
  NOT_IN_REGISTRY: 'not_in_registry',
  // Several listed feeds could be meant, and the response doesn't say which.
  AMBIGUOUS: 'ambiguous',
};

const unresolved = (key, reason, candidates = []) => ({ feedId: null, key, reason, candidates });

// The full resolution: `{ feedId, key, reason, candidates }`. `feedId` is set and `reason` is null
// on success; on failure `feedId` is null and `reason` is a DRILL_DOWN_REASONS value. `candidates`
// is only populated for AMBIGUOUS, where naming the rival ids is the whole explanation.
export const resolveMemberFeed = (input, feeds) => {
  const key = input?.feedId || input?.key || null;
  if (!key) return unresolved(null, DRILL_DOWN_REASONS.NO_IDENTITY);

  // No list to check against: the key is the best guess available, and a 422 is a fine answer.
  if (!feeds || feeds.length === 0) return { feedId: key, key, reason: null, candidates: [] };
  if (feeds.includes(key)) return { feedId: key, key, reason: null, candidates: [] };

  const suffixMatches = feeds.filter((feedId) => feedId === key || feedId.endsWith(`_${key}`) || feedId.includes(key));
  if (suffixMatches.length === 1) return { feedId: suffixMatches[0], key, reason: null, candidates: [] };

  // Ambiguous or absent: better an explained refusal than a confident request for the wrong feed.
  if (suffixMatches.length > 1) return unresolved(key, DRILL_DOWN_REASONS.AMBIGUOUS, suffixMatches);
  return unresolved(key, DRILL_DOWN_REASONS.NOT_IN_REGISTRY);
};

export const resolveMemberFeedId = (input, feeds) => resolveMemberFeed(input, feeds).feedId;

// Whether pressing the marker will actually navigate. No longer gates the control — every marker
// is pressable, and one that can't navigate opens the explanation instead — but it still decides
// which action the tooltip and accessible name promise.
export const canDrillDown = (input, feeds) => resolveMemberFeedId(input, feeds) !== null;
